import { useState, useEffect, PropsWithChildren, useRef, ReactNode } from 'react'
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom'
import { Job, useProjectQuery } from '~/graphql/hooks/useProjectQuery'
import { useSmartReportQuery } from '~/graphql/hooks/useSmartReportQuery'
import { useDeleteJobMutation } from '~/graphql/hooks/useDeleteJobMutation'
import moment from 'moment'
import { LoaderCircleIcon, PlusIcon, CheckIcon } from 'lucide-react'
import { useVariableValue } from '@devcycle/react-client-sdk'
import { env } from '~/env.ts'

import ReviewsSettingsMenu from '~/components/Reviews/ReviewsSettingsMenu'
import ReviewSettingsDialog from '~/components/Reviews/ReviewSettingsDialog'
import { MenuSimple as MenuSimpleIcon } from '~/components/ui/icons/MenuSimple'
import { DrawerLayout, DrawerContent, DrawerMainContent } from '~/components/DrawerLayout'
import Button from '~/components/ui/Button'
import ReviewsList from '~/components/Reviews/ReviewsList'
// import DoctypeTabs from '~/components/DoctypeTabs'
import DownloadsMenuButton from '~/components/Reviews/DownloadsMenuButton'
import NewReviewDialog from '~/components/Reviews/NewReviewDialog'
import Badge, { BadgeProps } from '~/components/ui/Badge'
import { reviewTime } from '~/utilities/datetimeFormats'
import Skeleton from '~/components/ui/Skeleton'
import TipsForBetterResultsDialog from '~/components/Reviews/TipsForBetterResultsDialog'
import SmartReporting from '~/components/Reviews/SmartReporting'
import Mixpanel from '~/utilities/mixpanel'
import { useLocalStorage } from '@uidotdev/usehooks'
import scanningBlueprints from '~/assets/images/scanning-blueprints-gray.gif'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '~/components/ui/AlertDialog'
import { toast } from 'sonner'
import ChangesSubmittalsTabs from '~/components/ChangesSubmittalsTabs'
import { useQuery } from '@tanstack/react-query'
import { checkPdfCache } from '~/lib/serviceWorker/swMessenger'
import { useProcessedPagesQuery } from '~/graphql/hooks/useProcessedPagesQuery'
import { useDevcycle } from '~/hooks/useDevcycle'

import WebViewer from '@pdftron/webviewer'
import { getFirefoxVersion } from '~/utilities/utils'

const POLLING_INTERVAL_5_MINUTES = 5 * 60 * 1000
const ERROR_STATUSES = ['review error', 'setup error', 'smart matching error']
const IN_PROGRESS_STATUSES = [
  'setup',
  'review starting',
  'smart matching',
  'user matching',
  'quality control'
]

const Reviews = () => {
  const viewerDivRef = useRef<HTMLDivElement>(null)

  const { pathname } = useLocation()
  const isProcore = pathname.startsWith('/procore') ? '/procore' : ''
  const asideRef = useRef<HTMLDivElement>(null)
  const [isSmartOverlayEnabled] = useLocalStorage('smart-overlay-enabled', true)
  const [skipReviewTips] = useLocalStorage('skip_review_tips', false)

  const { isDevcycleInitialized } = useDevcycle()
  const isSmartReportEnabled = useVariableValue('smart-reporting-enabled', false)
  const isTextSmartDiffEnabled = useVariableValue('text-smart-diff', false)
  const isSubmittalsEnabled = useVariableValue('streamlining-submittals-enabled', false)
  const isApryseProdViewerEnabled = useVariableValue('apryse-prod-viewer-enabled', false)
  const isFreeTierEnded = useVariableValue('restricted-user-accounts', false)
  const isAllowedOrgDomain = useVariableValue('allowed-organization-domains', false)
  const isRestrictedUser =
    isDevcycleInitialized && (isFreeTierEnded || !isAllowedOrgDomain)

  const [createReviewOpen, setCreateReviewOpen] = useState<boolean>(false)
  const [asideOpen, setAsideOpen] = useState<boolean>(true)
  const [selectedReview, setSelectedReview] = useState<Job | null>(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState<boolean>(false)

  const navigate = useNavigate()
  const { orgId, projectId, reviewId } = useParams<{
    orgId: string
    projectId: string
    reviewId: string
  }>()

  const {
    project,
    loading: isProjectLoading,
    startPolling,
    stopPolling
  } = useProjectQuery({ projectId: parseInt(projectId!) })

  const { processedPages } = useProcessedPagesQuery(
    parseInt(projectId!),
    parseInt(reviewId!)
  )

  const {
    smartReport,
    loading: isReportLoading,
    startPolling: reportPolling,
    stopPolling: reportStopPolling
  } = useSmartReportQuery(parseInt(reviewId!), {
    skip: !reviewId,
    fetchPolicy: 'cache-and-network'
  })

  const { deleteJob, loading: isDeleting } = useDeleteJobMutation()

  const { data: isPdfCached, isLoading: isCheckingPdfCache } = useQuery({
    queryKey: ['isPdfCached', reviewId],
    queryFn: () => checkPdfCache(parseInt(reviewId!)),
    enabled: !!reviewId
  })

  const onDeleteconfirm = async () => {
    const response = await deleteJob(parseInt(reviewId!))
    if (response) {
      toast.success(`Review deleted.`)
      setIsDeleteConfirmOpen(false)
      // For some reason we need the timeout
      // so we can trigger the click from the button ref
      setTimeout(() => {
        asideRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
        navigate(`${isProcore}/org/${orgId}/project/${projectId}/reviews`)
      })
    }
  }

  useEffect(() => {
    if (reviewId && project?.jobs) {
      const review = project.jobs.find((job) => job.id.toString() === reviewId)

      setSelectedReview(review ?? null)

      const { label } = getReviewStatus(review?.statusDetail ?? '')

      if ('In Progress' === label) {
        startPolling(POLLING_INTERVAL_5_MINUTES)
        reportPolling(POLLING_INTERVAL_5_MINUTES)
      } else {
        stopPolling()
        reportStopPolling()
      }
    } else if (!reviewId && project?.jobs.length) {
      const review = project.jobs[0]
      navigate(`${isProcore}/org/${orgId}/project/${projectId}/reviews/${review.id}`, {
        replace: true
      })
    } else {
      setSelectedReview(null)
    }
  }, [
    isProcore,
    navigate,
    orgId,
    project?.jobs,
    projectId,
    reportPolling,
    reportStopPolling,
    reviewId,
    startPolling,
    stopPolling
  ])

  // Validations for conditional rendering
  const isSelectedReviewCompleted =
    getStatusforUI(selectedReview?.statusDetail) === 'finished'

  const isPageLoading = isProjectLoading || isReportLoading

  const shouldRenderDrawingSmartReport =
    isSmartReportEnabled && Boolean(smartReport.changes.length)

  const shouldRenderTextSmartReport = isTextSmartDiffEnabled && isSelectedReviewCompleted

  const shouldRenderCounter =
    isSmartReportEnabled || (selectedReview?.jobType === 'text' && isTextSmartDiffEnabled)

  // Render functions
  function renderProgressState() {
    return (
      <>
        <div
          className="flex justify-center items-center bg-muted bg-cover bg-center border border-divider rounded-md py-6"
          style={{
            backgroundImage:
              selectedReview!.statusDetail === 'Review Completed'
                ? `url(${selectedReview!.downloadUrls.preview})`
                : ''
          }}
        >
          {getStatusforUI(selectedReview!.statusDetail) !== 'finished' && (
            <div className="w-1/2 flex flex-col gap-4">
              {getStatusforUI(selectedReview!.statusDetail) === 'progress' && (
                <>
                  <h3 className="text-center font-semibold">
                    Your Review is in Progress!
                  </h3>
                  <div className="flex flex-col items-center gap-4">
                    <div className="flex flex-col justify-center text-sm">
                      {getStepsStatus(selectedReview).map((step) => (
                        <div
                          key={step.step}
                          className="flex gap-2 items-center justify-center"
                        >
                          <p>{step.label}</p>
                          {step.icon}
                          {step.icon && step.message && <p>({step.message})</p>}
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-col justify-center">
                      <img
                        className="mx-auto grayscale-50"
                        src={scanningBlueprints}
                        width={160}
                      />
                    </div>
                  </div>
                </>
              )}

              {getStatusforUI(selectedReview!.statusDetail) === 'error' && (
                <>
                  <h3 className="text-center font-semibold text-error">Uh Oh!</h3>

                  <div className="text-center text-sm text-error">
                    We encountered a problem with your review, You didn&apos;t do anything
                    wrong, but it might take a bit longer for us to get this review
                    completed. It should complete within an hour, but if it takes longer,
                    someone from MBUE will contact you.
                  </div>
                </>
              )}

              <p className="text-center text-sm">
                You can leave this page and your review will continue to process. Come
                back any time to check the status. You will receive an email when it is
                complete
              </p>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 mt-4">
          <h2 className="font-semibold">Processed Pages</h2>
          <div className="grid grid-cols-3 gap-2">
            {processedPages.map((page) => (
              <Link
                key={page.id}
                to={`/org/${orgId}/project/${projectId}/reviews/${reviewId}/single-page-viewer?documentUrl=${encodeURIComponent(page.pageUrl)}&pageNumber=${page.pageIndex}&pageTitle=${encodeURIComponent(page.pageTitle)}&pageNumberText=${encodeURIComponent(page.pageNumber.toString())}`}
              >
                <div className="flex flex-col justify-center border border-divider rounded-md py-2 px-4 hover:bg-gray-50 transition-colors duration-200 cursor-pointer w-full">
                  <p className="text-sm">Page {page.pageIndex + 1}</p>
                  <p className="text-sm text-nowrap overflow-y-auto text-ellipsis">
                    {page.pageNumber}: {page.pageTitle}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </>
    )
  }

  function renderReviewContent() {
    if (IN_PROGRESS_STATUSES.includes(selectedReview?.statusDetail ?? ''))
      return renderProgressState()

    if (selectedReview?.jobType === 'drawing' && shouldRenderDrawingSmartReport) {
      return (
        <SmartReporting
          data={smartReport}
          reportType={selectedReview.jobType as 'drawing' | 'text'}
        />
      )
    }

    if (selectedReview?.jobType === 'text' && shouldRenderTextSmartReport) {
      const includedColumns = [
        'id',
        'pageIndex',
        'sectionNumber',
        'sectionTitle',
        'category',
        'priority',
        'description'
      ]

      const defaultReport = { includedColumns, changes: [] }

      return (
        <SmartReporting
          data={smartReport.changes.length ? smartReport : defaultReport}
          reportType={selectedReview.jobType as 'drawing' | 'text'}
        />
      )
    }
  }

  useEffect(() => {
    if (!viewerDivRef.current) return
    if (!selectedReview) return
    if (
      !selectedReview.downloadUrls.overlay ||
      selectedReview.downloadUrls.overlay === ''
    )
      return

    let webviewerKey: string = env.VITE_WEBVIEWER_DEMO_TOKEN ?? ''
    if (isApryseProdViewerEnabled && env.VITE_WEBVIEWER_TOKEN) {
      webviewerKey = env.VITE_WEBVIEWER_TOKEN
    }

    const container = viewerDivRef.current

    WebViewer.Iframe(
      {
        path: '/webviewer-0ad25b9a',
        licenseKey: webviewerKey,
        preloadWorker: 'pdf',
        fullAPI: true,
        ui: 'legacy'
      },
      container
    )
      .then((webviewerInstance) => {
        const { Core } = webviewerInstance
        const { documentViewer } = Core

        const version = getFirefoxVersion()

        if (version && version > 145) {
          console.log('Disabling virtual display mode for Firefox version', version)
          const displayMode = Core.documentViewer.getDisplayModeManager()
          displayMode.disableVirtualDisplayMode()
        }

        void documentViewer.loadDocument(selectedReview.downloadUrls.overlay ?? '', {
          extension: 'pdf'
        })
      })
      .catch(() => null)

    return () => {
      container.innerHTML = ''
    }
  }, [selectedReview, isApryseProdViewerEnabled])

  return (
    <>
      <DrawerLayout open={asideOpen} setOpen={setAsideOpen} hideCloseButton={!reviewId}>
        <DrawerContent className="h-full flex flex-col w-[368px]">
          <div className="flex flex-col gap-4 px-6 py-4 border-b border-divider">
            {isSubmittalsEnabled && <ChangesSubmittalsTabs defaultTab="changes" />}
            <div className="flex justify-between items-center">
              <h4 className="text-xl font-semibold">Reviews</h4>

              <div className="flex gap-4">
                {skipReviewTips ? (
                  <Button
                    className="flex gap-2"
                    onClick={() => setCreateReviewOpen(true)}
                    disabled={isRestrictedUser}
                  >
                    <PlusIcon size={16} />
                    <span>New</span>
                  </Button>
                ) : (
                  <TipsForBetterResultsDialog onClose={() => setCreateReviewOpen(true)}>
                    <Button className="flex gap-2" disabled={isRestrictedUser}>
                      <PlusIcon size={16} />
                      <span>New</span>
                    </Button>
                  </TipsForBetterResultsDialog>
                )}
              </div>
            </div>
          </div>

          <div ref={asideRef} className="overflow-y-auto flex-1">
            <ReviewsList isLoading={isProjectLoading} jobs={project?.jobs ?? []} />
          </div>
        </DrawerContent>

        <DrawerMainContent className="w-full h-[calc(100vh_-_56px)]">
          {isPageLoading && <LoadingState />}

          {!isPageLoading && (project?.jobs ?? []).length === 0 && (
            <EmptyState
              setOpen={setCreateReviewOpen}
              isRestrictedUser={isRestrictedUser}
            />
          )}

          {!isPageLoading && selectedReview && (
            <div className="pr-6 pl-4 flex flex-col h-full pb-4">
              <div className="h-[72px] flex justify-between items-center border-b border-divider">
                <div className="flex gap-2 items-center">
                  <button
                    onClick={() => setAsideOpen((prevState) => !prevState)}
                    className={`border border-divider rounded-full p-[3px] ${asideOpen && 'hidden'}`}
                  >
                    <MenuSimpleIcon />
                  </button>

                  <h2 className="text-xl text-content font-semibold">
                    {selectedReview?.name ??
                      selectedReview?.revisedDesign.originalFilename}
                  </h2>

                  <ReviewsSettingsMenu
                    onClickEdit={() => setIsEditModalOpen(true)}
                    onClickDelete={() => setIsDeleteConfirmOpen(true)}
                  />
                </div>
                <div className="flex gap-2 items-center">
                  <DownloadsMenuButton
                    originalUrl={selectedReview.downloadUrls.original}
                    revisedUrl={selectedReview.downloadUrls.revised}
                    diffUrl={
                      isSmartOverlayEnabled && selectedReview.jobType === 'drawing'
                        ? selectedReview.downloadUrls.overlay
                        : selectedReview.downloadUrls.diff
                    }
                    smartReportUrl={selectedReview.downloadUrls.reportCsv}
                    status={selectedReview.statusDetail}
                    jobType={selectedReview.jobType}
                  />
                  <Button
                    asChild
                    variant="secondary"
                    aria-disabled={selectedReview.status !== 2}
                  >
                    <a
                      href={`/org/${orgId}/project/${projectId}/reviews/${reviewId}/view`}
                      onClick={(event) => {
                        if (selectedReview.status !== 2) {
                          event.preventDefault()
                          return
                        }
                        Mixpanel.track({
                          event: 'FE:view-results',
                          data: { mode: 'webviewer', type: 'diff' }
                        })
                      }}
                    >
                      View Results
                    </a>
                  </Button>
                </div>
              </div>

              <div className={`flex justify-between items-center py-2`}>
                <div className="grid">
                  <div>
                    <Badge
                      {...(getReviewStatus(
                        selectedReview.statusDetail ?? ''
                      ) as BadgeProps)}
                    />
                  </div>
                  <p className="text-sm text-disabled">
                    {shouldRenderCounter && isSelectedReviewCompleted ? (
                      <span>{smartReport.changes.length} Changes Found</span>
                    ) : (
                      <span>
                        {getDateLabel(selectedReview)}
                        {moment(
                          selectedReview.startedAt ??
                            selectedReview.completedAt ??
                            selectedReview.createdAt
                        ).fromNow()}
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-disabled">
                    Review Time:{' '}
                    {reviewTime(
                      selectedReview.startedAt ?? selectedReview.createdAt,
                      selectedReview.completedAt ?? ''
                    )}
                    {isSelectedReviewCompleted && isSmartReportEnabled && (
                      <span>
                        {' | '}
                        {selectedReview.completedAt ? 'Completed ' : 'Created '}
                        {moment(
                          selectedReview.completedAt ??
                            selectedReview.startedAt ??
                            selectedReview.createdAt
                        ).fromNow()}
                      </span>
                    )}
                  </p>
                </div>
              </div>

              {renderReviewContent()}
            </div>
          )}
        </DrawerMainContent>
      </DrawerLayout>

      <NewReviewDialog open={createReviewOpen} setOpen={setCreateReviewOpen} />

      <ReviewSettingsDialog
        reviewName={selectedReview?.name ?? ''}
        reviewId={selectedReview?.id ?? 0}
        open={isEditModalOpen}
        setOpen={setIsEditModalOpen}
      />

      <ConfirmDeleteDialog
        open={isDeleteConfirmOpen}
        setOpen={setIsDeleteConfirmOpen}
        onConfirm={onDeleteconfirm}
        loading={isDeleting}
        reviewName={
          selectedReview?.name ?? selectedReview?.revisedDesign.originalFilename
        }
      />

      {!isCheckingPdfCache && !isPdfCached && (
        <div className="fixed -top-[110%] -left-[110%] w-[70%] h-[70vh]">
          <div className="h-full w-full viewer" ref={viewerDivRef} />
        </div>
      )}
    </>
  )
}

export default Reviews

type ConfirmDialogProps = PropsWithChildren<{
  open?: boolean
  setOpen?: (open: boolean) => void
  onConfirm: () => Promise<void>
  loading?: boolean
  reviewName?: string
}>

export function ConfirmDeleteDialog({
  children,
  onConfirm,
  open,
  setOpen,
  loading,
  reviewName
}: ConfirmDialogProps) {
  return (
    <AlertDialog open={open}>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete &apos;{reviewName}&apos;? <br />
            This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setOpen?.(false)}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => void onConfirm()} disabled={loading}>
            {loading && <LoaderCircleIcon className="mr-2 h-5 w-5 animate-spin" />}
            Yes, Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function LoadingState() {
  return (
    <div className="px-4 flex flex-col h-full pb-4">
      <div className="border-b border-b-divider h-[72px] py-1">
        <Skeleton className="w-full h-full flex items-center justify-between px-2">
          <Skeleton className="w-1/3 h-9" />
          <Skeleton className="w-1/4 h-9" />
        </Skeleton>
      </div>
      <div className="flex-grow flex flex-col">
        <div className="py-1">
          <Skeleton className="w-full h-[72px] flex flex-col gap-[2px] justify-center px-4">
            <Skeleton className="w-[64px] h-4" />
            <Skeleton className="w-1/4 h-4" />
            <Skeleton className="w-1/4 h-4" />
          </Skeleton>
        </div>
        <div className="py-2 flex-grow">
          <Skeleton className="w-full h-full grid place-content-center">
            <Skeleton className="h-10 w-32" />
          </Skeleton>
        </div>
      </div>
    </div>
  )
}

function EmptyState({
  setOpen,
  isRestrictedUser = false
}: {
  setOpen: (open: boolean) => void
  isRestrictedUser: boolean
}) {
  const [skipReviewTips] = useLocalStorage('skip_review_tips', false)
  return (
    <div className="w-full h-[calc(100vh_-_56px)] grid place-content-center">
      <div className="grid gap-2">
        <p>Select a Review to view it, or</p>
        {skipReviewTips ? (
          <Button
            className="flex gap-2"
            onClick={() => setOpen(true)}
            disabled={isRestrictedUser}
          >
            <PlusIcon size={16} />
            <span>New</span>
          </Button>
        ) : (
          <TipsForBetterResultsDialog onClose={() => setOpen(true)}>
            <Button className="flex gap-1" disabled={isRestrictedUser}>
              <PlusIcon size={16} />
              <span>Start a new Review</span>
            </Button>
          </TipsForBetterResultsDialog>
        )}
      </div>
    </div>
  )
}

function getReviewStatus(status: string): { label: string; variant: string } {
  if (!status) return { label: 'unknown', variant: 'neutral' }

  if (ERROR_STATUSES.includes(status.toLowerCase()))
    return { label: 'Error', variant: 'error' }

  if (IN_PROGRESS_STATUSES.includes(status.toLowerCase()))
    return { label: 'In Progress', variant: 'warning' }

  return { label: 'Completed', variant: 'success' }
}

function getStatusforUI(status: string | undefined): 'progress' | 'finished' | 'error' {
  if (!status) return 'finished'

  if (ERROR_STATUSES.includes(status.toLowerCase())) return 'error'

  if (IN_PROGRESS_STATUSES.includes(status.toLowerCase())) return 'progress'

  // if(['review complete'].includes(status.toLowerCase()))
  return 'finished'
}

function getDateLabel(review: Job): string {
  if (new Date(review.startedAt ?? review.createdAt) > new Date(review.completedAt ?? ''))
    return 'Started '
  if (new Date(review.completedAt ?? '') > new Date(review.startedAt ?? review.createdAt))
    return 'Completed '
  return 'In Progress '
}

interface StepStatus {
  step: string
  label: string
  icon: ReactNode
  message: string
}

function getStepsStatus(review: Job | null | undefined): StepStatus[] {
  if (!review) return []

  const status = review.statusDetail ?? 'setup'
  const totalPages = review.matchedPages
  const completedPages = review.completedPages

  const analyzingStatuses = ['setup', 'review starting']
  const matchingStatuses = ['smart matching']
  const comparingStatuses = ['user matching']
  const finalizingStatuses = ['quality control']

  const isAnalyzing = analyzingStatuses.includes(status)
  const isMatching = matchingStatuses.includes(status)
  const isComparing = comparingStatuses.includes(status) && completedPages < totalPages
  const hasError = ERROR_STATUSES.includes(status.toLowerCase())
  const isFinalizing = finalizingStatuses.includes(status)

  const getIcons = () => {
    if (isAnalyzing)
      return [
        <LoaderCircleIcon key="analyzingDocuments" size={14} className="animate-spin" />,
        null,
        null,
        null
      ]

    if (isMatching)
      return [
        <CheckIcon key="analyzingDocuments" size={14} />,
        <LoaderCircleIcon key="matchingPages" size={14} className="animate-spin" />,
        null,
        null
      ]

    if (isComparing)
      return [
        <CheckIcon key="analyzingDocuments" size={14} />,
        <CheckIcon key="matchingPages" size={14} />,
        <LoaderCircleIcon key="comparingPages" size={14} className="animate-spin" />,
        null
      ]

    if (isFinalizing)
      return [
        <CheckIcon key="analyzingDocuments" size={14} />,
        <CheckIcon key="matchingPages" size={14} />,
        <CheckIcon key="comparingPages" size={14} />,
        <LoaderCircleIcon key="finalizingOutput" size={14} className="animate-spin" />
      ]

    return [
      <CheckIcon key="analyzingDocuments" size={14} />,
      <CheckIcon key="matchingPages" size={14} />,
      <CheckIcon key="comparingPages" size={14} />,
      <CheckIcon key="finalizingOutput" size={14} />
    ]
  }

  return [
    {
      step: 'analyzingDocuments',
      label: 'Analyzing Documents...',
      icon: getIcons()[0],
      message: ''
    },
    {
      step: 'matchingPages',
      label: 'Matching Pages...',
      icon: getIcons()[1],
      message: ''
    },
    {
      step: 'comparingPages',
      label: 'Comparing Pages...',
      icon: getIcons()[2],
      message: hasError
        ? ''
        : `${totalPages - completedPages} out of ${totalPages} completed`
    },
    {
      step: 'finalizingOutput',
      label: 'Finalizing Output...',
      icon: getIcons()[3],
      message: ''
    }
  ]
}
